import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Alert,
  Stack,
  useTheme
} from '@mui/material';
import { 
  Security as SecurityIcon, 
  Logout as LogoutIcon,
  Warning as WarningIcon 
} from '@mui/icons-material';
import { useThemes } from '../../components/Theme/theme';

const NewLoginDetected = () => {
  const navigate = useNavigate();
  const { theme } = useThemes();
  const muiTheme = useTheme();

  const handleLogout = () => {
    // Replace current history entry to prevent back navigation to this page
    navigate('/logout', { replace: true });
  };

  const handleContinue = () => {
    // Replace current history entry to prevent back navigation to this page
    navigate('/logout', { replace: true });
  };

  // Handle browser navigation attempts
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Allow navigation but show warning
      e.preventDefault();
      e.returnValue = 'You will be logged out due to security reasons.';
      return 'You will be logged out due to security reasons.';
    };

    const handlePopState = (e) => {
      // Prevent back navigation and redirect to logout
      e.preventDefault();
      handleLogout();
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Auto logout after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleLogout();
    }, 30000); // 30 seconds

    return () => {
      clearTimeout(timer);
      handleLogout()
      // Don't call handleLogout here as it can cause navigation issues during unmount
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        overflow: 'hidden',
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: muiTheme.palette.warning.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>

            {/* Title */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: muiTheme.palette.text.primary,
                mb: 1,
              }}
            >
              New Login Detected
            </Typography>

            {/* Alert */}
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ 
                width: '100%',
                '& .MuiAlert-message': {
                  fontSize: '1rem',
                }
              }}
            >
              A new login has been detected on another device. For security reasons, this session will be terminated.
            </Alert>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: muiTheme.palette.text.secondary,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              This security measure helps protect your account from unauthorized access. 
              You will be automatically logged out and redirected to the login page.
            </Typography>

            {/* Additional Info */}
            <Box
              sx={{
                backgroundColor: muiTheme.palette.grey[50],
                borderRadius: 2,
                p: 2,
                width: '100%',
                border: `1px solid ${muiTheme.palette.grey[200]}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>What happens next:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • This session will be terminated<br/>
                • You'll be redirected to the login page<br/>
                • You can log in again with your credentials
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ width: '100%', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinue}
                startIcon={<LogoutIcon />}
                sx={{
                  flex: 1,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                }}
              >
                Continue to Login
              </Button>
            </Stack>

            {/* Auto logout countdown */}
            <Typography variant="caption" color="text.secondary">
              You will be automatically redirected in 30 seconds
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NewLoginDetected;
